'use client';

import { type TextareaHTMLAttributes, useCallback, useEffect, useRef } from 'react';

import { T } from '@transifex/react';

import { Container } from '../Input/Container';

import styles from './Textarea.module.scss';

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  /**
   * The translated string to use for the aria-label of the icon that clears the input.
   */
  ariaLabelClearInput: string;

  /**
   * The translated string or <T> element to use when there is an error with value entered into the input.
   */
  errorText?: React.ReactNode;

  /**
   * The state to represent if the input should be disabled.
   */
  isDisabled?: boolean;

  /**
   * The translated string or <T> element to use as the input's label.
   */
  labelText: React.ReactNode;

  /**
   * Hide the label visually, but keep it accessible for screen readers.
   */
  hideLabel?: boolean;

  /**
   * The function used to clear the value entered into the input.
   */
  onClear: () => void;

  /**
   * The amount of rows the textarea should initially render.
   */
  rows?: number;

  /**
   * The translated string or <T> element to use as the input's sublabel (e.g. info about password restrictions).
   */
  supportingText?: React.ReactNode;

  /**
   * Maximum character count allowed. Defaults to 1500.
   */
  maxLength?: number;

  /**
   * Whether to show the character counter. Defaults to true.
   */
  showCharacterCount?: boolean;
};

export const Textarea = ({
  ariaLabelClearInput,
  className,
  errorText,
  isDisabled,
  labelText,
  hideLabel = false,
  name,
  onChange,
  onClear,
  placeholder,
  rows = 1,
  supportingText,
  value,
  maxLength = 1500,
  showCharacterCount = false,
  ...inputProps
}: TextareaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const characterCount = typeof value === 'string' ? value.length : 0;
  const isOverLimit = characterCount >= maxLength;

  const resizeTextarea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const padding =
        parseFloat(window.getComputedStyle(textareaRef.current).getPropertyValue('padding-top')) +
        parseFloat(window.getComputedStyle(textareaRef.current).getPropertyValue('padding-bottom'));
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight - padding}px`;
    }
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(event);
      }
      resizeTextarea();
    },
    [onChange, resizeTextarea],
  );

  useEffect(() => {
    if (textareaRef.current && value === '') {
      resizeTextarea();
    }
  }, [value, resizeTextarea]);

  return (
    <div className={styles['textarea-wrapper']}>
      <Container
        ariaLabelClearInput={ariaLabelClearInput}
        className={className}
        errorText={errorText}
        isDisabled={isDisabled}
        labelText={labelText}
        hideLabel={hideLabel}
        name={name}
        onClear={onClear}
        supportingText={supportingText}
      >
        <textarea
          disabled={isDisabled}
          id={name}
          name={name}
          onChange={handleChange}
          placeholder={placeholder}
          ref={textareaRef}
          value={value}
          rows={rows}
          maxLength={maxLength}
          aria-invalid={isOverLimit || !!errorText}
          {...inputProps}
        />
      </Container>
      {showCharacterCount && (
        <div className={styles['character-count-wrapper']}>
          {isOverLimit && (
            <div className={styles['error-text']}>
              <T _str="Character Limit Reached" _key="form.character_limit_reached" _tags="form" />
            </div>
          )}
          <div className={`${styles['character-count']} ${isOverLimit ? styles['over-limit'] : ''}`}>
            {characterCount} / {maxLength}
          </div>
        </div>
      )}
    </div>
  );
};
